import React, { useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { crd } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill } from "../../constants/gradCard";
import { formatPropAddr } from "../../utils/propertyAddr";
import TxnTable from "../common/TxnTable";

function SpEarnings({ sp }) {
  const { t, h } = useContext(Ctx);
  if (sp.loading) return <div style={{ paddingTop: 40, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 13 }}>{t.syncingDb}</div></div>;
  const spPayments = sp.data?.payments || []; const spJobs = sp.data?.jobs || []; const spTens = sp.data?.tens || []; const spProps = sp.data?.props || [];

  const completedJobs = spJobs.filter(j => j.sp_status === 'completed' || j.confirmed_by_tenant);

  // Build the deduped earnings list once. service_payments rows are
  // authoritative when present; otherwise fall back to a maintenance
  // row's sp_pay_amt so SP can see what they're owed before landlord
  // settles. All counters/totals derive from this so we don't double-
  // count a job that exists in both tables.
  const paidMaintIds = new Set(spPayments.map(p => p.maint_id).filter(Boolean));
  const merged = [
    ...spPayments.map(p => {
      const job = spJobs.find(j => j.id === p.maint_id);
      return {
        key: p.id, desc: job?.desc || t.jobPayment,
        tid: job?.tid, amount: p.amount || 0, st: p.st, dt: p.dt, notes: p.notes,
      };
    }),
    ...completedJobs
      .filter(j => (j.sp_pay_amt || 0) > 0 && !paidMaintIds.has(j.id))
      .map(j => ({
        key: `job_${j.id}`, desc: j.desc,
        tid: j.tid, amount: j.sp_pay_amt || 0,
        st: j.sp_pay_st === 'paid' ? 'paid' : 'pending',
        dt: j.dt, notes: null,
      })),
  ].sort((a, b) => (b.dt || b.key || "").localeCompare(a.dt || a.key || ""));

  const paidEntries = merged.filter(it => it.st === 'paid');
  const pendingEntries = merged.filter(it => it.st !== 'paid');
  const combinedPaid = paidEntries.reduce((s, it) => s + it.amount, 0);
  const combinedPending = pendingEntries.reduce((s, it) => s + it.amount, 0);
  const combinedTotal = combinedPaid + combinedPending;

  return <div style={{ paddingTop: 16 }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>{t.earnings}</h2>
    {/* Summary banner */}
    <div style={{ ...gradCardStyle("green"), borderRadius: 14, padding: "16px", marginBottom: 16, textAlign: "center" }}>
      <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginBottom: 4 }}>{t.totalEarnings}</div>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: 28 }}>₹{combinedTotal.toLocaleString()}</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 10 }}>
        <div><div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>₹{combinedPaid.toLocaleString()}</div><div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10 }}>{t.paid}</div></div>
        <div style={{ width: 1, background: "rgba(255,255,255,0.3)" }}></div>
        <div><div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>₹{combinedPending.toLocaleString()}</div><div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10 }}>{t.pending}</div></div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <div style={{ flex: 1, ...gradCardStyle("indigo"), borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>{merged.length}</div>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10 }}>{t.totalTransactions || "Transactions"}</div>
      </div>
      <div style={{ flex: 1, ...gradCardStyle("green"), borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>{paidEntries.length}</div>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10 }}>{t.paid}</div>
      </div>
      <div style={{ flex: 1, ...gradCardStyle(pendingEntries.length > 0 ? "amber" : "green"), borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>{pendingEntries.length}</div>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10 }}>{t.pending}</div>
      </div>
    </div>
    {merged.length === 0 && <div style={{ color: h.sc, textAlign: "center", padding: 40, fontSize: 13 }}>{t.noEarnings}</div>}
    {merged.length > 0 && <>
      <div style={{ color: h.tx, fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{t.paymentHistory || "Payment History"}</div>

      {/* Reusable transactions table. Cards view renders the rich card
          layout the SP is used to; list view is the compact spreadsheet.
          Range filter + sort + CSV/PDF download all live in TxnTable. */}
      <TxnTable
        rows={merged}
        h={h}
        t={t}
        title="Service Earnings"
        exportFilename="sp-earnings"
        dateField="dt"
        defaultSortKey="dt"
        defaultSortDir="desc"
        columns={[
          { key: "dt", label: "Date", type: "date" },
          { key: "desc", label: "Job", type: "text" },
          { key: "amount", label: "Amount", type: "number", align: "right",
            format: (r) => `₹${(r.amount || 0).toLocaleString()}` },
          { key: "st", label: "Status", type: "text",
            format: (r) => {
              const sc = r.st === "paid" ? h.gn : h.wn;
              return <span style={{ color: sc, fontWeight: 600, fontSize: 11, padding: "2px 6px", borderRadius: 6, background: `${sc}15` }}>
                {r.st === "paid" ? t.paid : t.pending}
              </span>;
            }
          },
          { key: "notes", label: "Notes", type: "text",
            format: (r) => r.notes ? <span title={r.notes} style={{ color: h.sc }}>{r.notes.length > 30 ? r.notes.slice(0, 30) + "…" : r.notes}</span> : "—" },
        ]}
        renderCard={(it) => {
          const ten = it.tid ? spTens.find(x => x.id === it.tid) : null;
          const prop = ten ? spProps.find(x => x.id === ten.pid) : null;
          const tone = it.st === 'paid' ? "green" : "amber";
          return <div key={it.key}
            onMouseOver={(e) => gradLift(e, tone, true)}
            onMouseOut={(e) => gradLift(e, tone, false)}
            style={{ ...crd(h), ...gradCardStyle(tone) }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{it.desc}</div>
                {ten && <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>{ten.name}{prop ? ` · ${formatPropAddr(prop)}` : ""}</div>}
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 10, marginTop: 2 }}>{it.dt}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>₹{(it.amount || 0).toLocaleString()}</div>
                <span style={glassPill}>{it.st === 'paid' ? t.paid : t.pending}</span>
              </div>
            </div>
            {it.notes && <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.25)" }}>{it.notes}</div>}
          </div>;
        }}
      />
    </>}
  </div>;
}

export default SpEarnings;
